import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';

import { getCatalogDuplicates } from '../../api/url_data_agent_evaluations';
import { getShopProductUrl } from '../../helpers/helpers';
import {
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_RANGE,
  formatDate,
  formatNumber,
  getDateRangeParams
} from '../../helpers/url_data_agent_evaluations_helpers';
import { getProductAdminUrl } from '../../helpers/url_data_agent_live_matches_helpers';
import { useSearchParamsState } from '../../hooks/useSearchParamsState';
import { downloadTextFile } from '../Catalogs/catalogArtifactFiles';
import LiveMatchRowSkeleton from './LiveMatchRowSkeleton';
import PreviewThumb from './PreviewThumb';
import SegmentedControl from './SegmentedControl';

import './SilentEvalCatalogDuplicates.scss';

const LIMIT = 500;
const URL_DEFAULTS = { range: DEFAULT_DATE_RANGE };

const RELATIONS = {
  sibling: { label: 'Shared retailer link', detail: 'Both catalog products are attached to the same retailer link.' },
  duplicate_title: { label: 'Same title and brand', detail: 'The titles and brands match. Check for different colors, sizes or styles.' },
  adjudicated: {
    label: 'AI flagged a duplicate',
    detail: 'An AI comparison judged these to be the same product. The catalog team still needs to check.'
  }
};

const CSV_COLUMNS = [
  ['product_a_id', ({ first }) => first.id],
  ['product_a_title', ({ first }) => first.title],
  ['product_a_admin_url', ({ first }) => getProductAdminUrl(first.id)],
  ['product_b_id', ({ second }) => second.id],
  ['product_b_title', ({ second }) => second.title],
  ['product_b_admin_url', ({ second }) => getProductAdminUrl(second.id)],
  ['brand', ({ first, second }) => first.brand || second.brand],
  ['reason', ({ pair }) => RELATIONS[pair.relation]?.label || pair.relation],
  ['times_flagged', ({ pair }) => pair.evidenceCount],
  ['domains', ({ pair }) => pair.domains.join(' ')],
  ['example_url', ({ pair }) => pair.exampleUrls[0]],
  ['last_seen', ({ pair }) => pair.lastSeenAt]
];

const splitPair = pair => ({ pair, first: pair.products[0], second: pair.products[1] });

const csvCell = value => {
  const text = value == null ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// Export product comparisons for the catalog team to review.
export const buildCsv = pairs =>
  [
    CSV_COLUMNS.map(([header]) => header).join(','),
    ...pairs.map(splitPair).map(split => CSV_COLUMNS.map(([, read]) => csvCell(read(split))).join(','))
  ].join('\n');

const SilentEvalCatalogDuplicates = ({ active = true, onCount }) => {
  const [urlState, patchUrl] = useSearchParamsState(URL_DEFAULTS);
  const dateRange = DATE_RANGE_OPTIONS.some(option => option.value === urlState.range) ? urlState.range : DEFAULT_DATE_RANGE;
  const setDateRange = value => patchUrl({ range: value });
  const [resultsByRange, setResultsByRange] = useState({});
  const [errorsByRange, setErrorsByRange] = useState({});
  const [fetchingRange, setFetchingRange] = useState(null);
  const result = resultsByRange[dateRange];
  const error = errorsByRange[dateRange];
  // One fetch happens even while hidden so the tab count is known before the tab is opened.
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!active && hasFetchedRef.current) return undefined;
    hasFetchedRef.current = true;
    let cancelled = false;
    setFetchingRange(dateRange);
    getCatalogDuplicates({ ...getDateRangeParams(dateRange), limit: LIMIT })
      .then(data => {
        if (cancelled) return;
        setResultsByRange(cur => ({ ...cur, [dateRange]: data }));
        setErrorsByRange(cur => ({ ...cur, [dateRange]: null }));
        onCount(data?.total || 0);
      })
      .catch(err => {
        if (!cancelled) setErrorsByRange(cur => ({ ...cur, [dateRange]: err || 'Could not load duplicate pairs.' }));
      })
      .finally(() => {
        if (!cancelled) setFetchingRange(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dateRange, active, onCount]);

  const pairs = result?.pairs || [];
  const total = result?.total || 0;
  const isTruncated = total > pairs.length;
  const summaryLine =
    result &&
    [
      `${formatNumber(total)} possible duplicate ${total === 1 ? 'pair' : 'pairs'}`,
      isTruncated && `showing the first ${formatNumber(pairs.length)} here and in the CSV`
    ]
      .filter(Boolean)
      .join(' · ');
  const download = () =>
    downloadTextFile(
      `catalog-duplicates-${new Date().toISOString().slice(0, 10)}${isTruncated ? `-first-${pairs.length}-of-${total}` : ''}.csv`,
      buildCsv(pairs)
    );

  return (
    <div className='silent-eval-duplicates'>
      <div className='se-toolbar'>
        <SegmentedControl label='Date range' options={DATE_RANGE_OPTIONS} value={dateRange} onChange={setDateRange} />
        {summaryLine && <span className='dup-summary'>{summaryLine}</span>}
        <button type='button' className='se-btn' disabled={!pairs.length} onClick={download}>
          Export CSV
        </button>
        <span className={cn('se-refreshing', { on: fetchingRange != null })} aria-hidden='true' />
      </div>

      <p className='dup-intro'>
        APM and a reviewer picked different catalog entries for the same link. These may be duplicates. Compare the products and retailer page before
        making changes.
      </p>

      {!result ? (
        error ? (
          <div className='se-empty'>Could not load duplicate pairs — {String(error)}</div>
        ) : (
          <div className='dup-list'>
            {_.range(3).map(i => (
              <LiveMatchRowSkeleton key={i} />
            ))}
          </div>
        )
      ) : !pairs.length ? (
        <div className='se-empty'>No possible duplicates in this period.</div>
      ) : (
        <div className='dup-list'>
          {pairs.map(pair => (
            <PairRow key={pair.key} {...splitPair(pair)} />
          ))}
        </div>
      )}
    </div>
  );
};

SilentEvalCatalogDuplicates.propTypes = {
  active: PropTypes.bool,
  onCount: PropTypes.func.isRequired
};

const PairRow = ({ pair, first, second }) => {
  const [expanded, setExpanded] = useState(false);
  const relation = RELATIONS[pair.relation] || { label: pair.relation, detail: pair.relation };
  const evidenceTooltip = `Last flagged ${formatDate(pair.lastSeenAt)}`;
  const detailsId = `duplicate-${pair.key}`;

  return (
    <>
      <div className='dup-row'>
        <PreviewThumb source={first.image} title={first.title} subtitle={first.brand} />
        <ProductText product={first} role='Product A' />
        <span className='dup-glyph' aria-hidden='true'>
          ⇄
        </span>
        <PreviewThumb source={second.image} title={second.title} subtitle={second.brand} />
        <ProductText product={second} role='Product B' />
        <span className={cn('dup-relation', pair.relation)} title={relation.detail}>
          {relation.label}
        </span>
        <span className='dup-evidence' title={evidenceTooltip}>
          Flagged {formatNumber(pair.evidenceCount)} {pair.evidenceCount === 1 ? 'time' : 'times'} · {pair.domains.join(', ')}
        </span>
        <button type='button' className='se-btn' aria-expanded={expanded} aria-controls={detailsId} onClick={() => setExpanded(value => !value)}>
          {expanded ? 'Close review' : 'Review products'}
        </button>
      </div>
      {expanded && (
        <div className='dup-review' id={detailsId}>
          <p>{relation.detail} Check the brand, style, color and size before deciding whether both entries should exist.</p>
          <div className='dup-review-links'>
            <a className='se-btn' href={getProductAdminUrl(first.id)} target='_blank' rel='noopener noreferrer'>
              Edit product A ↗
            </a>
            <a className='se-btn' href={getProductAdminUrl(second.id)} target='_blank' rel='noopener noreferrer'>
              Edit product B ↗
            </a>
            {pair.exampleUrls.map((url, index) => (
              <a key={url} className='se-btn' href={/^https?:\/\//i.test(url) ? url : `https://${url}`} target='_blank' rel='noopener noreferrer'>
                Retailer page{pair.exampleUrls.length > 1 ? ` ${index + 1}` : ''} ↗
              </a>
            ))}
            <button
              type='button'
              className='se-btn'
              onClick={() => downloadTextFile(`catalog-duplicate-${pair.key.replace(':', '-')}.csv`, buildCsv([pair]))}
            >
              Export this pair
            </button>
          </div>
          <p>
            Use the product editor to correct details. If the entries need merging, export the pair for catalog cleanup. This list does not merge
            products.
          </p>
        </div>
      )}
    </>
  );
};

PairRow.propTypes = {
  pair: PropTypes.object.isRequired,
  first: PropTypes.object.isRequired,
  second: PropTypes.object.isRequired
};

const ProductText = ({ product, role }) => (
  <div className='dup-text'>
    <div className='dup-title' title={product.title}>
      <a href={getShopProductUrl(product.id)} target='_blank' rel='noopener noreferrer'>
        {product.title}
      </a>
    </div>
    <div className='dup-meta'>
      <span className='dup-role'>{role}</span>
      <span>{product.brand || 'Unknown brand'}</span>
      {product.category?.name && <span>{product.category.name}</span>}
      <a className='mono' href={getProductAdminUrl(product.id)} target='_blank' rel='noopener noreferrer'>
        #{product.id}
      </a>
    </div>
  </div>
);

ProductText.propTypes = {
  product: PropTypes.object.isRequired,
  role: PropTypes.oneOf(['Product A', 'Product B']).isRequired
};

export default SilentEvalCatalogDuplicates;
